import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * PLU Filter - Debounced input, persistent filtering
 * Filter stays active until admin manually clicks Clear button
 * IMPROVED: Maintains focus during parent re-renders with longer debounce
 */
function PLUFilter({ 
  placeholder = "Filter by Customer #",
  onFilterChange,
  label = "Filter",
  debounceMs = 800  // Increased from 500ms to give more time for typing
}) {
  const [localValue, setLocalValue] = useState('');
  const timerRef = useRef(null);
  const lastAppliedValueRef = useRef('');
  const inputRef = useRef(null);
  const isFocusedRef = useRef(false);
  
  // Track focus state more reliably
  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);
  
  const handleBlur = useCallback(() => {
    // Small delay before marking as unfocused to handle click-to-button scenarios
    setTimeout(() => {
      isFocusedRef.current = false;
    }, 200);
  }, []);
  
  // Restore focus after potential re-renders (silently)
  useEffect(() => {
    // If the input was focused before a re-render, restore focus
    if (isFocusedRef.current && inputRef.current && document.activeElement !== inputRef.current) {
      // Use requestAnimationFrame to avoid layout thrashing
      requestAnimationFrame(() => {
        if (isFocusedRef.current && inputRef.current) {
          inputRef.current.focus();
          // Also restore cursor position to end
          const len = inputRef.current.value.length;
          inputRef.current.setSelectionRange(len, len);
        }
      });
    }
  });
  
  // Cleanup timer on unmount only
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);
  
  // Apply filter after debounce, but don't clear it
  useEffect(() => {
    // Clear any pending timer
    if (timerRef.current) clearTimeout(timerRef.current);
    
    // Set new timer to apply the filter
    timerRef.current = setTimeout(() => {
      // Only call parent if value actually changed
      if (localValue !== lastAppliedValueRef.current) {
        lastAppliedValueRef.current = localValue;
        if (onFilterChange && typeof onFilterChange === 'function') {
          onFilterChange(localValue);
        }
      }
    }, debounceMs);
    
    // DON'T cleanup on every render - this was causing the reset
  }, [localValue, debounceMs, onFilterChange]);
  
  const handleClear = useCallback(() => {
    setLocalValue('');
    lastAppliedValueRef.current = '';
    if (timerRef.current) clearTimeout(timerRef.current);
    // Clear immediately
    if (onFilterChange && typeof onFilterChange === 'function') {
      onFilterChange('');
    }
    // Keep focus on input after clearing
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [onFilterChange]);
  
  // Memoized onChange handler to prevent unnecessary re-renders
  const handleChange = useCallback((e) => {
    setLocalValue(e.target.value);
  }, []);
  
  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    // Escape to clear
    if (e.key === 'Escape') {
      handleClear();
    }
    // Enter to apply immediately
    if (e.key === 'Enter') {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (localValue !== lastAppliedValueRef.current) {
        lastAppliedValueRef.current = localValue;
        if (onFilterChange && typeof onFilterChange === 'function') {
          onFilterChange(localValue);
        }
      }
    }
  }, [handleClear, localValue, onFilterChange]);
  
  return (
    <div style={{
      background: 'white',
      padding: '1rem',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      maxWidth: '400px'
    }}>
      {label && (
        <label style={{fontWeight: '600', color: '#333', fontSize: '14px', whiteSpace: 'nowrap'}}>
          {label}:
        </label>
      )}
      
      <div style={{display: 'flex', alignItems: 'center', flex: 1, border: '1px solid #ccc', borderRadius: '4px', overflow: 'hidden'}}>
        <span style={{
          padding: '0.6rem 0.5rem',
          background: '#f5f5f5',
          color: '#666',
          fontWeight: '600',
          fontSize: '14px',
          borderRight: '1px solid #ccc'
        }}>
          PLU-
        </span>
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={localValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          style={{
            padding: '0.6rem',
            border: 'none',
            flex: 1,
            fontSize: '14px',
            color: '#000',
            outline: 'none',
            background: 'white'
          }}
        />
      </div>
      
      <button
        onClick={handleClear}
        type="button"
        style={{
          background: '#6c757d',
          color: 'white',
          border: 'none',
          padding: '0.6rem 1rem',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '500',
          whiteSpace: 'nowrap'
        }}
      >
        Clear
      </button>
    </div>
  );
}

export default React.memo(PLUFilter);
